import { useCallback } from "react";
import { useAppRoutes } from "../../routes/useAppRoutes";
import { GroupListPaginationContext } from "../../groups/context";
import { usePaginationContext, type PaginationInterpreter } from "../../common/state";
import { LensListPaginationContext } from "../../lenses/context";
import { type CameraKitOrganization } from "../../../gql-generated/types";
import { useOrganizationService as useOrganizationsService } from "./useOrganizationsService";
import { useSelectedOrganization } from "./useSelectedOrganization";

export function useSwitchOrganization() {
    const { navigateToOrganization, navigateToOrganizationApps } = useAppRoutes();
    const organizationService = useOrganizationsService();

    const { reset: resetGroupsPagination } = usePaginationContext(
        GroupListPaginationContext as React.Context<PaginationInterpreter>,
    );
    const { reset: resetLensesPagination } = usePaginationContext(
        LensListPaginationContext as React.Context<PaginationInterpreter>,
    );
    const { selectedOrganizationId } = useSelectedOrganization();

    const handleRedirect = useCallback(
        (redirectToApps = false, orgId?: string) => {
            if (redirectToApps) {
                navigateToOrganizationApps(orgId ?? selectedOrganizationId ?? "");
            } else {
                navigateToOrganization();
            }
        },
        [navigateToOrganization, navigateToOrganizationApps, selectedOrganizationId],
    );

    const switchOrganization = useCallback(
        async (organization?: Readonly<CameraKitOrganization | null>) => {
            if (!organization) {
                return;
            }
            try {
                await organizationService.switchOrganization(organization);
                resetGroupsPagination();
                resetLensesPagination();
            } catch (e) {
                console.error(e);
            }
        },
        [organizationService, resetGroupsPagination, resetLensesPagination],
    );

    return {
        handleRedirect,
        switchOrganization,
        selectedOrganizationId,
    };
}
